
import React, { useState, useEffect, useCallback } from 'react';
import type { VehicleInfo } from '../types';
import { vehicleService, type VehicleData } from '../services/vehicleService';
import { aiService } from '../services/aiServiceFactory';
import { validateVehicleDiagnosticInput } from '../services/inputValidationService';

interface InitialFormProps {
    onStartDiagnosis: (info: VehicleInfo) => void;
    onReset?: number;
}

interface UploadedImage {
    name: string;
    content: string;
    mimeType: string;
}

interface DtcHistoryItem {
  code: string;
  result: string;
}

export const InitialForm: React.FC<InitialFormProps> = ({ onStartDiagnosis, onReset }) => {
    const [formData, setFormData] = useState<Omit<VehicleInfo, 'files'>>({
        make: '',
        model: '',
        year: '',
        faultDescription: '',
        dtcCodes: ''
    });
    const [isProcessingImages, setIsProcessingImages] = useState(false);
    const [imageError, setImageError] = useState<string | null>(null);
    const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
    const [faultDescriptionError, setFaultDescriptionError] = useState<string | null>(null);
    
    // State for vehicle data
    const [vehicleData, setVehicleData] = useState<VehicleData>({});
    const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
    const [vehicleError, setVehicleError] = useState<string | null>(null);
    
    // State for DTC lookup
    const [dtcLookupCode, setDtcLookupCode] = useState('');
    const [dtcLookupResult, setDtcLookupResult] = useState<string | null>(null);
    const [isDtcLoading, setIsDtcLoading] = useState(false);
    const [dtcLookupError, setDtcLookupError] = useState<string | null>(null);
    const [dtcHistory, setDtcHistory] = useState<DtcHistoryItem[]>([]);

    // Reset function to clear all form data and state
    const resetForm = useCallback(() => {
        setFormData({
            make: '',
            model: '',
            year: '',
            faultDescription: '',
            dtcCodes: ''
        });
        setUploadedImages([]);
        setFaultDescriptionError(null);
        setImageError(null);
        setDtcLookupCode('');
        setDtcLookupResult(null);
        setDtcLookupError(null);
        // Note: We don't reset DTC history as it's stored in localStorage and might be useful to keep
    }, []);

    // Handle external reset calls
    useEffect(() => {
        if (onReset && onReset > 0) {
            resetForm();
        }
    }, [onReset, resetForm]);

    useEffect(() => {
        // Load DTC history
        try {
            const storedHistory = localStorage.getItem('dtcLookupHistory');
            if (storedHistory) {
                setDtcHistory(JSON.parse(storedHistory));
            }
        } catch (error) {
            console.error("Failed to load DTC history from localStorage", error);
            localStorage.removeItem('dtcLookupHistory');
        }

        // Load vehicle data
        const loadVehicleData = async () => {
            setIsLoadingVehicles(true);
            setVehicleError(null);
            try {
                const data = await vehicleService.getAllVehicles();
                setVehicleData(data);
            } catch (error) {
                console.error('Failed to load vehicle data:', error);
                setVehicleError('Failed to load vehicle data. Using fallback data.');
            } finally {
                setIsLoadingVehicles(false);
            }
        };

        loadVehicleData();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        try {
            const { name, value } = e.target;

            if (name === 'make') {
                setFormData({
                    ...formData,
                    make: value,
                    model: '',
                    year: ''
                });
            } else if (name === 'model') {
                setFormData({
                    ...formData,
                    model: value,
                    year: ''
                });
            } else {
                setFormData(prev => ({ ...prev, [name]: value }));
                
                // Validate fault description when it changes
                if (name === 'faultDescription') {
                    if (value.trim()) {
                        const validation = validateVehicleDiagnosticInput(value);
                        if (!validation.isValid) {
                            setFaultDescriptionError(validation.errorMessage || 'Please provide a valid vehicle diagnostic description.');
                        } else {
                            setFaultDescriptionError(null);
                        }
                    } else {
                        setFaultDescriptionError(null);
                    }
                }
            }
        } catch (error) {
            console.error('Error in handleChange:', error);
            // Don't crash the app, just log the error
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsProcessingImages(true);
        setImageError(null);

        const filePromises = Array.from(files).map((file: File) => {
            return new Promise<{ image: UploadedImage, description: string }>((resolve, reject) => {
                if (file.size > 4 * 1024 * 1024) { // 4MB limit
                    reject(new Error(`${file.name} is too large (max 4MB).`));
                    return;
                }

                const reader = new FileReader();
                reader.onload = async () => {
                    try {
                        const base64Content = (reader.result as string).split(',')[1];
                        const description = await aiService.analyzePartImagesForFault(base64Content, file.type);
                        resolve({
                            image: { name: file.name, content: base64Content, mimeType: file.type },
                            description
                        });
                    } catch (err) {
                        reject(err);
                    }
                };
                reader.onerror = (error) => reject(error);
                reader.readAsDataURL(file);
            });
        });

        try {
            const results = await Promise.all(filePromises);
            const newImages = results.map(r => r.image);
            const newDescriptions = results.map(r => r.description).filter(Boolean);

            setUploadedImages(prev => [...prev, ...newImages]);
            if (newDescriptions.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    faultDescription: [prev.faultDescription, ...newDescriptions].filter(Boolean).join('\n')
                }));
            }
        } catch (err) {
            console.error("Image processing error:", err);
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
            setImageError(`Could not analyze image(s): ${errorMessage}`);
        } finally {
            setIsProcessingImages(false);
            e.target.value = ''; // Reset file input
        }
    };
    
    const handleRemoveImage = (index: number) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const dataToSend: VehicleInfo = { 
            ...formData,
            files: uploadedImages.map(img => ({ content: img.content, mimeType: img.mimeType }))
        };
        onStartDiagnosis(dataToSend);
    };

    const handleDtcLookup = async (e: React.FormEvent) => {
        e.preventDefault();
        const codeToLookup = dtcLookupCode.trim().toUpperCase();
        if (!codeToLookup) return;

        setIsDtcLoading(true);
        setDtcLookupResult(null);
        setDtcLookupError(null);

        try {
            const result = await aiService.lookupDtcCode(codeToLookup);
            setDtcLookupResult(result);

            // Update history
            const newHistoryItem = { code: codeToLookup, result };
            setDtcHistory(prevHistory => {
                const filteredHistory = prevHistory.filter(item => item.code !== newHistoryItem.code);
                const updatedHistory = [newHistoryItem, ...filteredHistory].slice(0, 10);
                try {
                    localStorage.setItem('dtcLookupHistory', JSON.stringify(updatedHistory));
                } catch (error) {
                    console.error("Failed to save DTC history to localStorage", error);
                }
                return updatedHistory;
            });

        } catch (err) {
            console.error("DTC lookup error:", err);
            setDtcLookupError('Failed to look up code. Please try again.');
        } finally {
            setIsDtcLoading(false);
        }
    };

    const handleHistoryClick = (item: DtcHistoryItem) => {
        setDtcLookupCode(item.code);
        setDtcLookupResult(item.result);
        setDtcLookupError(null);
    };

    const handleClearHistory = () => {
        setDtcHistory([]);
        setDtcLookupResult(null);
        try {
            localStorage.removeItem('dtcLookupHistory');
        } catch (error) {
            console.error("Failed to clear DTC history from localStorage", error);
        }
    };

    const makes = Object.keys(vehicleData).sort();
    
    // Handle both array and object formats for models
    const getModelsForMake = (make: string): string[] => {
        const makeData = vehicleData[make];
        if (!makeData) return [];
        
        // If it's an array (simple format), return the array
        if (Array.isArray(makeData)) {
            return makeData.sort();
        }
        
        // If it's an object (detailed format), return the keys
        return Object.keys(makeData).sort();
    };
    
    const models = formData.make ? getModelsForMake(formData.make) : [];
    
    // Handle years extraction for both formats
    const getYearsForMakeModel = (make: string, model: string): number[] => {
        const makeData = vehicleData[make];
        if (!makeData || !model) return [];
        
        // If it's an array (simple format), no years available
        if (Array.isArray(makeData)) {
            return [];
        }
        
        // If it's an object (detailed format), get years for the model
        return makeData[model] || [];
    };
    
    const years = formData.make && formData.model ? getYearsForMakeModel(formData.make, formData.model) : [];
    
    const isFormValid = formData.make && formData.model && formData.faultDescription && (years.length === 0 || formData.year) && !faultDescriptionError;

    const selectClassName = "mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#002A5C] focus:border-[#002A5C] disabled:bg-gray-50 disabled:text-gray-400";

    return (
        <div className="p-6 sm:p-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-1">Vehicle Information</h2>
            <p className="text-sm text-gray-500 mb-6">Provide the details below to start the diagnosis.</p>
            
            {isLoadingVehicles && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                        <span className="text-sm text-blue-700">Loading vehicle data...</span>
                    </div>
                </div>
            )}
            
            {vehicleError && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-700">{vehicleError}</p>
                </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
                <fieldset disabled={isProcessingImages || isLoadingVehicles}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label htmlFor="make" className="block text-sm font-medium text-gray-600">Make</label>
                            <select name="make" id="make" value={formData.make} onChange={handleChange} required className={selectClassName}>
                                <option value="" disabled>Select Make</option>
                                {makes.map(make => <option key={make} value={make}>{make}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="model" className="block text-sm font-medium text-gray-600">Model</label>
                            <select name="model" id="model" value={formData.model} onChange={handleChange} required disabled={!formData.make} className={selectClassName}>
                                <option value="" disabled>Select Model</option>
                                {models.map(model => <option key={model} value={model}>{model}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="year" className="block text-sm font-medium text-gray-600">Year</label>
                            <select name="year" id="year" value={formData.year} onChange={handleChange} required={years.length > 0} disabled={!formData.model || years.length === 0} className={selectClassName}>
                                <option value="" disabled>Select Year</option>
                                {years.length > 0 ? (
                                    years.sort((a, b) => b - a).map(year => <option key={year} value={year}>{year}</option>)
                                ) : (
                                    <option value="" disabled>No years available</option>
                                )}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="faultDescription" className="block text-sm font-medium text-gray-600">Fault Description</label>
                        
                        {/* Validation Error Display */}
                        {faultDescriptionError && (
                            <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{faultDescriptionError}</p>
                            </div>
                        )}
                        
                        <textarea 
                            name="faultDescription" 
                            id="faultDescription" 
                            value={formData.faultDescription} 
                            onChange={handleChange} 
                            required 
                            rows={3} 
                            className={`mt-1 block w-full px-3 py-2 bg-white border rounded-md text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#002A5C] focus:border-[#002A5C] ${
                                faultDescriptionError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                            placeholder="Describe the symptom or upload a part image below..."
                        ></textarea>
                    </div>

                    <div>
                        <label htmlFor="dtcCodes" className="block text-sm font-medium text-gray-600">DTC Codes (Optional)</label>
                        <input type="text" name="dtcCodes" id="dtcCodes" value={formData.dtcCodes} onChange={handleChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#002A5C] focus:border-[#002A5C]" placeholder="e.g., P0300, P0420" />
                    </div>
                </fieldset>
                
                <div className="pt-2">
                    <button type="submit" disabled={!isFormValid || isProcessingImages || isLoadingVehicles} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#002A5C] hover:bg-[#001F44] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed">
                        {isProcessingImages ? 'Processing Images...' : isLoadingVehicles ? 'Loading...' : 'Start Diagnosis'}
                    </button>
                </div>
            </form>
            
            <div className="mt-6">
                <h3 className="text-md font-semibold text-gray-700">Visual Diagnosis (Optional)</h3>
                <p className="text-sm text-gray-500 mb-3">Upload images of the faulty part to get an AI-generated description.</p>
                <label htmlFor="part-image-upload" className="w-full cursor-pointer flex justify-center items-center space-x-2 py-2 px-4 border border-dashed border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Upload Part Image(s)</span>
                </label>
                <input id="part-image-upload" name="part-image-upload" type="file" className="sr-only" onChange={handleImageUpload} accept="image/*" disabled={isProcessingImages} multiple />
            </div>

            {isProcessingImages && (
                <div className="flex justify-center items-center mt-4 space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-t-2 border-[#002A5C]"></div>
                    <p className="text-sm text-gray-500">Analyzing image(s)...</p>
                </div>
            )}

            {imageError && <p className="mt-2 text-sm text-center text-red-600">{imageError}</p>}

            {uploadedImages.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {uploadedImages.map((image, index) => (
                        <div key={index} className="relative group aspect-square">
                            <img 
                                src={`data:${image.mimeType};base64,${image.content}`} 
                                alt={image.name} 
                                className="w-full h-full object-cover rounded-md border border-gray-200"
                            />
                            <button
                                onClick={() => handleRemoveImage(index)}
                                className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                aria-label={`Remove ${image.name}`}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-6 border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-1">DTC Code Lookup</h3>
                <p className="text-sm text-gray-500 mb-4">Enter a code to get a quick explanation.</p>
                <form onSubmit={handleDtcLookup} className="flex items-start space-x-2">
                    <div className="flex-grow">
                        <label htmlFor="dtc-lookup" className="sr-only">DTC Code</label>
                        <input
                            type="text"
                            id="dtc-lookup"
                            value={dtcLookupCode}
                            onChange={(e) => setDtcLookupCode(e.target.value)}
                            className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#002A5C] focus:border-[#002A5C]"
                            placeholder="e.g., P0300"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={!dtcLookupCode.trim() || isDtcLoading}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#E31B23] hover:bg-[#B8161C] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                        style={{ minWidth: '80px', height: '40px' }}
                    >
                        {isDtcLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                            'Look Up'
                        )}
                    </button>
                </form>

                {dtcHistory.length > 0 && (
                    <div className="mt-4">
                        <div className="flex justify-between items-center mb-2">
                            <h4 className="text-sm font-semibold text-gray-600">Recent Lookups</h4>
                            <button
                                onClick={handleClearHistory}
                                className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 rounded hover:bg-red-50"
                                aria-label="Clear DTC lookup history"
                            >
                                Clear History
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {dtcHistory.map((item) => (
                                <button
                                    key={item.code}
                                    onClick={() => handleHistoryClick(item)}
                                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
                                >
                                    {item.code}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {isDtcLoading && !dtcLookupResult && !dtcLookupError && (
                    <div className="mt-4 text-center text-sm text-gray-500">
                        Searching...
                    </div>
                )}

                {dtcLookupError && (
                     <p className="mt-4 text-sm text-center text-red-600 bg-red-50 p-3 rounded-md">{dtcLookupError}</p>
                )}

                {dtcLookupResult && (
                    <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                        <h4 className="font-semibold text-gray-800 mb-2">Result for {dtcLookupCode.toUpperCase()}</h4>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{dtcLookupResult}</p>
                    </div>
                )}
            </div>
        </div>
    );
};
